import React, { useState } from 'react';
import { Outlet, useNavigate } from 'react-router-dom';
import {
  Box,
  AppBar,
  Toolbar,
  Typography,
  IconButton,
  Avatar,
  Menu,
  MenuItem,
  useTheme,
  useMediaQuery,
} from '@mui/material';
import {
  Menu as MenuIcon,
  AccountCircle,
  Logout,
} from '@mui/icons-material';
import { useAuth } from '../context/AuthContext';
import CollapsibleSidebar from './CollapsibleSidebar';

const CleanLayout = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const navigate = useNavigate();
  const [sidebarOpen, setSidebarOpen] = useState(!isMobile);
  const [anchorEl, setAnchorEl] = useState(null);
  const { user, logout } = useAuth();

  const handleMenuOpen = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const handleLogout = () => {
    logout();
    handleMenuClose();
    navigate('/login');
  };

  const handleSidebarToggle = () => {
    setSidebarOpen(!sidebarOpen);
  };

  const drawerWidth = sidebarOpen ? 280 : 80;

  return (
    <Box className="no-print" sx={{ display: 'flex', minHeight: '100vh', position: 'relative' }}>
      {/* Sidebar */}
      <CollapsibleSidebar
        open={sidebarOpen}
        onToggle={setSidebarOpen}
        variant={isMobile ? 'temporary' : 'persistent'}
      />

      {/* Main Content Area */}
      <Box
        component="main"
        sx={{
          flexGrow: 1,
          width: '100%',
          display: 'flex',
          flexDirection: 'column',
          minHeight: '100vh',
        }}
      >
        {/* Top AppBar */}
        <AppBar
          position="fixed"
          sx={{
            backgroundColor: '#ffffff',
            color: '#3D3B54',
            boxShadow: '0 1px 3px rgba(0,0,0,0.12)',
            zIndex: theme.zIndex.drawer + 1,
            width: '100%',
          }}
        >
          <Toolbar sx={{ minHeight: 64 }}>
            {/* Mobile Menu Toggle */}
            {isMobile && (
              <IconButton
                edge="start"
                onClick={handleSidebarToggle}
                sx={{ mr: 2 }}
              >
                <MenuIcon />
              </IconButton>
            )}

            {/* App Title */}
            <Box sx={{ flexGrow: 1, display: 'flex', alignItems: 'center' }}>
              <Typography
                variant="h6"
                sx={{
                  fontWeight: 700,
                  color: '#3D3B54',
                  fontSize: '1.25rem',
                }}
              >
                Eye Clinic Management System
              </Typography>
            </Box>

            {/* User Menu */}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              {user && (
                <Typography
                  variant="body2"
                  sx={{ 
                    display: { xs: 'none', sm: 'block' },
                    color: '#7B7A92',
                    fontWeight: 500,
                  }}
                >
                  {user.name}
                </Typography>
              )}
              <IconButton
                onClick={handleMenuOpen}
                size="small"
                sx={{
                  '&:hover': {
                    backgroundColor: 'rgba(99, 102, 241, 0.04)',
                  },
                }}
              >
                <Avatar
                  sx={{
                    width: 36,
                    height: 36,
                    bgcolor: 'primary.main',
                    fontSize: '0.875rem',
                    fontWeight: 600,
                  }}
                >
                  {user?.name?.charAt(0) || 'U'}
                </Avatar>
              </IconButton>
            </Box>

            {/* User Dropdown Menu */}
            <Menu
              anchorEl={anchorEl}
              open={Boolean(anchorEl)}
              onClose={handleMenuClose}
              anchorOrigin={{
                vertical: 'bottom',
                horizontal: 'right',
              }}
              transformOrigin={{
                vertical: 'top',
                horizontal: 'right',
              }}
              PaperProps={{
                sx: {
                  mt: 1,
                  borderRadius: '12px',
                  boxShadow: '0 10px 25px rgba(0,0,0,0.1)',
                  minWidth: 200,
                },
              }}
            >
              <MenuItem disabled sx={{ opacity: 0.6 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                  <AccountCircle sx={{ color: '#7B7A92' }} />
                  <Box>
                    <Typography variant="body2" sx={{ fontWeight: 600 }}>
                      {user?.name}
                    </Typography>
                    <Typography variant="caption" sx={{ color: '#7B7A92' }}>
                      {user?.email}
                    </Typography>
                  </Box>
                </Box>
              </MenuItem>
              <MenuItem onClick={handleLogout}>
                <Logout sx={{ mr: 1, color: '#D98B8B' }} />
                <Typography variant="body2" sx={{ color: '#D98B8B' }}>
                  Logout
                </Typography>
              </MenuItem>
            </Menu>
          </Toolbar>
        </AppBar>

        {/* Page Content */}
        <Box
          sx={{
            flexGrow: 1,
            backgroundColor: '#FAFAFD',
            overflow: 'auto',
            marginTop: '64px',
            height: 'calc(100vh - 64px)',
          }}
        >
          <Box
            sx={{
              maxWidth: '100%',
              padding: { xs: '16px', sm: '24px', md: '32px' },
            }}
          >
            <Outlet />
          </Box>
        </Box>
      </Box>
    </Box>
  );
};

export default CleanLayout;
